import { ChordLib } from '@tunasong/music-lib'
import type { FunctionCategory, QuizQuestion } from '@tunasong/music-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { FunctionQuizAnswerCard } from './function-quiz-answer-card.js'
import type { AnswerCardState } from './function-quiz-answer-card.js'
import styles from './function-quiz-question.module.css'

const CATEGORIES: FunctionCategory[] = ['tonic', 'predominant', 'dominant']

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const scaleLabel = (question: QuizQuestion): string => `${question.scale.root} ${capitalize(question.scale.type ?? 'major')}`

export interface FunctionQuizQuestionProps {
  question: QuizQuestion
  scoreCorrect: number
  scoreAnswered: number
  /**
   * When false, the answer cards are rendered in a non-clickable (`dimmed`)
   * state. Used during the `feedback` phase where the question stays visible
   * but the user has already answered. The Hear-it button stays interactive
   * in both modes.
   */
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: FunctionCategory) => void
}

/**
 * Function Quiz v1 question panel — chord name + key context + Hear-it
 * audition button + 3 answer cards. Stateless; the host owns session state
 * and feeds in `interactive`. Header surfaces a running-score chip and the
 * lexicon-driven prompt copy.
 */
export const FunctionQuizQuestion: FC<FunctionQuizQuestionProps> = props => {
  const { question, scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  const promptCopy = useTheoryCopy('function-quiz-prompt')
  const chordName = ChordLib.getName(question.chord)
  const cardState: AnswerCardState = interactive ? 'idle' : 'dimmed'
  return (
    <section className={styles.question} aria-label="Quiz question">
      <header className={styles.header}>
        <h2 className={styles.title}>Function Quiz</h2>
        <span
          className={styles.scoreChip}
          aria-label={`${scoreCorrect} correct out of ${scoreAnswered} answered`}
        >
          {scoreCorrect} / {scoreAnswered}
        </span>
      </header>
      <p className={styles.prompt}>{promptCopy?.body ?? "What's this chord's function?"}</p>
      <div className={styles.chordLine}>
        <span className={styles.chordName}>{chordName}</span>
        <span className={styles.keyContext}>in {scaleLabel(question)}</span>
        <button type="button" className={styles.hearItButton} onClick={onHearIt}>
          ▶ Hear it
        </button>
      </div>
      <div className={styles.cardGrid}>
        {CATEGORIES.map(cat => (
          <FunctionQuizAnswerCard key={cat} category={cat} state={cardState} onPick={onAnswer} />
        ))}
      </div>
    </section>
  )
}
